import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import ResourceManager from '@/Management/ResourceManager';
import ModalChangeHeaderTitleMessage from '@/Components/ModalDetailView/ModalChangeHeaderTitleMessage';
import Messenger from '@/Utility/Messenger';
import DetailViewEnteredMessage from '@/Messages/DetailViewEnteredMessage';
import Loader from '@/Utility/Loader';
import DetailViewExitedMessage from '@/Messages/DetailViewExitedMessage';
import ToolBarLinkViewModel from '@/Components/Links/ToolBarLinkViewModel';
import { IDetailViewContextDTO } from "coreframetypings/dto";
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import Utilities from '@/Utility/Utilities';
import LocatorHelper from '@/Utility/LocatorHelper';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

export default class ModalDetailViewContext extends DetailViewContext {

    private detailViewContextCounter: number;

    constructor(params: any) {
        super($.extend(params, {
            autoInit: false
        }));
        this.detailViewContextCounter = this.paramOrDefault(params.detailViewContextCounter, 0);
        this._contextParameters = $.extend(this._contextParameters, { IS_MODAL_DV: true });
        this.initDetailViewContext();
    }

    /**
     * @override
     */
    public raiseChangeHeader(): void {
        Messenger.send(new ModalChangeHeaderTitleMessage({
            context: this,
            title: this.pageTitle,
            prefix: ResourceManager.getInstance().get("DETAILVIEW_PAGETITLE_PREFIX")
        }));
    }

    /**
     * @override
     */
    protected checkDispose(): boolean {
        return this.viewModel == null || this.viewModel.isDisposed;
    }

    /**
     * @override
     */
    protected createDetailViewEnteredMessage(hasError: boolean): DetailViewEnteredMessage {
        const msg = super.createDetailViewEnteredMessage(hasError);
        msg.isModalDetailView = true;
        return msg;
    }

    /**
     * @override
     */
    protected createDetailViewExitedMessage(): DetailViewExitedMessage {
        const msg = super.createDetailViewExitedMessage();
        msg.isModalDetailView = true;
        return msg;
    }

    /**
     * @override
     */
    protected createPrimaryToolBarLinks(detailViewContext: IDetailViewContextDTO): ToolBarLinkViewModel[] {
        let linkViewModels = [];

        linkViewModels.push(new ToolBarLinkViewModel({
            context: this,
            ergName: 'OKAY',
            imageSource: GlobalConfiguration.inlineImages.DV_SAVE,
            parentLocator: LocatorHelper.buildLocator('modaldv', this.detailViewContextCounter, detailViewContext.detailView.typeName),
            locator: 'ok',
            tooltipErgName: 'MODAL_OKAY_TOOLTIP',
            isEnabled: this.defineComputed(() => {
                return detailViewContext.lockAquired && detailViewContext.hasWriteRight;
            }),
            onClick: () => {
                // Vorher den Focus verlassen, um sicher zu stellen, dass alle Inputs den Focus verloren haben und die Änderung übernommen haben.
                $(document.activeElement).blur();

                this.saveModal();
            },
            showToolBarText: true
        }));

        linkViewModels.push(new ToolBarLinkViewModel({
            context: this,
            ergName: 'DISCARD',
            imageSource: GlobalConfiguration.inlineImages.CANCEL_OFF,
            parentLocator: LocatorHelper.buildLocator('modaldv', this.detailViewContextCounter, detailViewContext.detailView.typeName),
            locator: 'discard',
            tooltipErgName: 'MODAL_DISCARD_TOOLTIP',
            isEnabled: true,
            onClick: () => {
                this.cancelModal();
            },
            showToolBarText: true
        }));

        return linkViewModels;
    }

    /**
     * @override
     */
    protected closeCurrentContext(): JQueryPromise<boolean> {
        let deferred = $.Deferred();

        Loader.getInstance().show();

        this.viewModel.removeCurrentContext((currentContext) => {
            Loader.getInstance().hide();

            deferred.resolve(true);
        });

        return deferred.promise();
    }

    public saveModal(): JQueryPromise<boolean> {
        Loader.getInstance().show();

        return RADEnvironmentProxy.getInstance().whenAllRequestsAreResponded().then(async () => {
            if (this.isDisposedOrDisposing())
                return false;

            this.resetValidationErrors();

            const allowSave = await this.callOnBeforeSaveUIListener();
            if (allowSave === false || this.isDisposedOrDisposing())
                return false;

            const allowApplyUIListener = await this.callOnBeforeApplyUIListener();
            if (allowApplyUIListener === false || this.isDisposedOrDisposing())
                return false;

            const allowApply = await this.onBeforeApply();
            if (allowApply === false || this.isDisposedOrDisposing())
                return false;

            const updateSuccess = await this.updateAndValidateProperties();
            if (updateSuccess === false || this.isDisposedOrDisposing())
                return false;

            const applySuccess = await this.applyDetailViewContext(false);
            if (applySuccess === false || this.isDisposedOrDisposing())
                return false;

            return this.isOnStackNavigation
                ? true
                : this.closeCurrentContext();
        }).always(() => {
            Loader.getInstance().hide();
        }).promise();
    }

    public saveModalBeforeStackNavigation(): JQueryPromise<boolean> {
        this.isOnStackNavigation = true;
        return this.saveModal().always((success) => {
            this.isOnStackNavigation = success;
        }).promise();
    }

    public cancelModal(): JQueryPromise<boolean> {
        if (this._isCanceling)
            return Utilities.resolveImmediately(false);

        Loader.getInstance().show()

        this._isCanceling = true;

        return RADEnvironmentProxy.getInstance().whenAllRequestsAreResponded().then(() => {
            return $.when(this.callOnBeforeCancelUIListener()).then((allowCancel) => {
                if (allowCancel !== false) {
                    return $.when(this._revertPendingChanges()).then((revertSuccess) => {
                        if (revertSuccess !== false) {
                            this._latestDetailViewAction = 'Cancel';
                            return this.closeCurrentContext();
                        }

                        return revertSuccess;
                    });
                }

                return allowCancel;
            }).then((result) => {
                return result;
            });
        }).always(() => {
            Loader.getInstance().hide();
        }).promise();
    }

    /**
     * @override
     */
    public notifyDetailViewClosed(): void {
        Messenger.createMessageAndSend('DetailViewExitedMessage', this.createDetailViewExitedMessage());
        Messenger.disposeMessage('DetailViewEnteredMessage');
        Messenger.disposeMessage('DetailViewExitedMessage');

        this._hasPendingDVC = false;
        this._isContextClosed = true;

        return this.uiListener['onDetailViewClosedInternal'](this);
    }
}